import { FormulaEngineInstance, VisionStep } from '@wizeoo/akm-message-lib';
import { WizConstants } from '../../model/constants';
import { VisionVO } from '../vision/vision.vo';
import { Model } from '../../model/model';
import { WidgetLine } from './widget-line.obj';
import { FeiKeyDef } from './fei-key-definition.obj';
import { Triggers } from '../../model/interfaces';
import { MeasMode } from '../../manager/computer/measure.mode.obj';
import { COMPUTE_MODE } from '../../model/enums';
import { FreedataValueVO } from '../../vo/core/freedatavalue.vo';

/**
 * Contrat d'interface pour un VO représentant une instance de moteur de calcul pour un niveau de step
 */
export class FeiVO {

    /* Le modèle */
    protected _model: Model;
    public get model(): Model {
        return this._model;
    }
    public set model(value: Model) {
        this._model = value;
    }

    /** constructeur */
    constructor(pSource: FormulaEngineInstance, model: Model, pVision: VisionVO, pStep: VisionStep, pScen: string) {
        this.source = pSource;
        this._model = model;
        this.vision = pVision;
        this.visionStep = pStep;
        this.scenario = pScen || WizConstants.BASE_SCENARIO;
        this.uniqueName = [this.visionStep.level, this.scenario].join(WizConstants.CONTEXT_SEPARATOR);
    }

    /** L'objet source provenant du back */
    public source: FormulaEngineInstance;

    /** le nom unique level#scen#  */
    public uniqueName: string = null;

    /** la vision VisionVO pour mémorisation des maps calculées */
    public vision: VisionVO = null;

    /** le step */
    public visionStep: VisionStep = null;

    get visionCode(): string {
        return this.vision.code;
    }

    public get visionStepCode(): string {
        return this.visionStep.code;
    }

    public get visionStepLevel(): number {
        return this.visionStep.level;
    }

    /** mode tooling pour IncExc et vsp. Code du tool à utiliser pour la mesure */
    public toolMode: string = null; // construction des lignes inc/exc, ou sélection VSP, mode sample, pas de génération des lignes S, F, T

    /** mode formulaire FDV. Clone en cours pour prise de valeur des triggers sur slaveBaseline */
    public fakeFDV: FreedataValueVO = null;

    /** mode de calcul local ou compute */
    public computeMode: COMPUTE_MODE = COMPUTE_MODE.LOCAL;
    /** pages chargées en mode compute / widget puisque les confs sont différentes */
    public pages: Map<string, Map<number, WidgetLine[]>>;
    /** taille de page en mode compute / widget */
    public pageSize: Map<string, Set<number>>;
    /** tailles totales par widgets */
    public totalSizes: Map<string, number>;
    /** date du calcul */
    public computeTime: number;
    /** numéro de révision compute */
    public computeRevNumber: number;
    /** page demandée pour un widget */
    public requestPage: Map<string, number>;



    /**
     * map des grilles de poids des baselines pour un code de grille donné
     * forme codeGrille = baselineKey(parent) = [baselineEnfant] => poids
     * alimenté lors de la construction des poids dans le split
     */
    public weightMap: Map<string, Map<string, Map<string, number>>>;

    /** la définition des champs, alimentée sur buildKeys, déclenché par le dataEngine */
    public visionKeys: FeiKeyDef[] = []; // les keys, ordonnées
    public filterKeys: FeiKeyDef[] = []; // les filterKeys
    // toutes les clés
    public keys: FeiKeyDef[] = null;
    // uniquement les mesures éditables et les filterKey
    public editableKeys: Map<string, FeiKeyDef> = null;
    /** map des clés : key, filters, mesures par code pkd */
    public keyMap: Map<string, FeiKeyDef> = null;

    /** map des clés pour collecte des dépendances sur mesures */
    public allvisionKeys: Set<string> = null;

    /** valeurs par défaut et triggers pour chaque champ, par clé de champ#level */
    public triggersMap: Map<string, Triggers> = null;

    /** trigger inversé : les mesures sources avec les mesures à déclencher */
    public reverseTriggersMap: Map<string, Set<Triggers>> = null;


    /** le scenario */
    public scenario: string;

    public matrix: Map<string, MeasMode> = null; // la matrice de calcul de ce Fei, contient les MM avec leur dépendances
    public leveledMatrix: Map<number, Map<string, MeasMode>> = null; // matrice par niveau de calcul (pour vue mesure)
    public orderedMatrix: MeasMode[] = null; // les mm de la matrice dans l'ordre de calcul requis (par level croissant)
    
    public translatableEntries: Set<string> = null; // les mm traductibles (tous, les directs ET hérités)
    public translatableModeMatrix : MeasMode[] = null; // les mm de la matrice qui dépendent de mesures traductibles (pas les héritages)

    public requiredStep: Set<number> = new Set(); // les niveaux de dépendances (inherit, collect...) alimenté par formulaCoreEngine
    public requiredScenario: Set<string> = new Set(); // les scenarii nécessaires
    public requiredFei: Map<number, Map<string, FeiVO>> = new Map(); // la résultante avec les feis dont je dépend

    public requiredCrossed: Set<string> = new Set();

    // indicateur de complétion de préparation
    public linesLoaded: boolean = false; // les lignes du fei cible et step cible sont dispos
    public scenariiDependenciesLoaded: boolean = false; // toutes les dépendances de scenarii sont chargées
    public stepDependenciesLoaded: boolean = false; // toutes les dépendances de step sont chargées
    public crossDependenciesLoaded: boolean = false; // toutes les dépendances crossVision sont chargées

    public allDependenciesReady: boolean = false; // toutes les dépendances sont dispos
    public workingPoolReady: boolean = false; // mon pool de ligne est prêt
    public computing: boolean = false; // demande de calcul effectuée
    public computed: boolean = false; // tout est calculé

    // [COMPUTE] calculé, peut-être... Mais pour quelle locale (et à quel niveau de révision) ?
    public computedLocales: Map<string, any> = null; 
    // [COMPUTE] écart identifier sur la locale, il faudra recalculer les translatableEntries
    public localeDeprecated: boolean = false;

    // la pile de parents pour le scenario de ce FEI
    // utilisé pour la construction des lines
    // null en cas de multiScen
    // sous la forme [papa, grand-père..., MAIN]
    public parentScenariiPile: string[];

    // map des lignes (non aggrégées de ce FEI)
    // alimenté depuis les Baselines des scenarii
    // par DataEngine.feedWorkingPool
    // scen = key = WidgetLine. 
    // dans le cas d'un mono-scenar (ou pas de scenar), une seule entrée pour scen (celui du FEI)
    // la ventilation par scenar est nécessaire pour le multiscen
    public lines: Map<string, Map<string, WidgetLine>>;

    /**
     * retourne la collection de lignes, à plat
     * @returns WidgetLine[]
     */
    public getAllLines(): WidgetLine[] {
        let result: WidgetLine[] = [];

        if (this.lines) {
            this.lines.forEach((mapLines, scen) => {
                result = result.concat([...mapLines.values()]);
            });
        } else {
            console.log('no lines ??', this);
        }
        return result;
    }

    /**
     * Retourne la WidgetLine pour la key en paramètre
     * attention, c'est une baseline potentiellement modifiée par le cumul de valeurs de scenarii
     * @param scen scenario cible
     * @param key clé de WL
     * @returns la WidgetLine
     */
    public getWidgetLineForKey(scen: string, key: string): WidgetLine {
        let res: WidgetLine = this.lines?.get(scen)?.get(key) || null;
        return res;
    }

    /** 
     * collection de WidgetLines par clé parente
     * sous la forme  scenario = clé parente = clé baseline enfant[] = WidgetLine
     */
    // public _widgetLinesByParentKey: Map<string, Map<string, Map<string, WidgetLine>>> = null;

    /** 
     * WidgetLines par :
     * scen => level cible
     * => clé de la BL mère => Map<key, WidgetLine>
     * Un peu comme parent, mais plus consommateur (ancestor) */
    public _widgetLinesByLevels: Map<string, Map<number, Map<string, Map<string, WidgetLine>>>> = new Map();


    /**
     * recherche de toutes les WidgetLines par clé réduites pour un niveau de step donné
     * va construire les splittedKeys si nécessaire
     * @param scen 
     * @param level 
     * @returns la map cléMère = Map<clés filles, WidgetLines>
     */
    public getAllWidgetLinesByLevel(scen: string, level: number): Map<string, Map<string, WidgetLine>> {
        if (!this._widgetLinesByLevels.get(scen)?.has(level)) { // existe pas encore pour le couple scenar / niveau
            if (!this._widgetLinesByLevels.has(scen)) {
                this._widgetLinesByLevels.set(scen, new Map());
            }
            if (!this._widgetLinesByLevels.get(scen).has(level)) {
                this._widgetLinesByLevels.get(scen).set(level, new Map());
            }

            if (this.lines?.get(scen)?.size > 0) {
                if (level === this.visionStepLevel - 1) { // c'est le niveau supérieur, on peut utiliser la pkey de la baseline
                    [...this.lines.get(scen).values()].forEach((wl: WidgetLine) => {
                        if (!this._widgetLinesByLevels.get(scen).get(level).has(wl.bl.pkey)) {
                            this._widgetLinesByLevels.get(scen).get(level).set(wl.bl.pkey, new Map());
                        }
                        this._widgetLinesByLevels.get(scen).get(level).get(wl.bl.pkey).set(wl.key, wl);
                    });
                } else { // un niveau antérieur, j'ai besoin du split sur la clé
                    let motherKey: string;
                    [...this.lines?.get(scen)?.values()].forEach((wl: WidgetLine) => {
                        // split de la clé si pas encore fait
                        if (!wl.bl.splittedKey) { // si le split n'est pas encore fait
                            wl.bl.splittedKey = wl.bl.key.split(WizConstants.KEY_SEPARATOR);
                        }
                        motherKey = wl.bl.splittedKey.slice(0, this.vision.keySizeByLevel.get(level)).join(WizConstants.KEY_SEPARATOR);

                        if (!this._widgetLinesByLevels.get(scen).get(level).has(motherKey)) {
                            this._widgetLinesByLevels.get(scen).get(level).set(motherKey, new Map());
                        }
                        this._widgetLinesByLevels.get(scen).get(level).get(motherKey).set(wl.key, wl);
                    });

                }
            }
        }
        return this._widgetLinesByLevels.get(scen).get(level);
    }

    /**
     * recherche des WidgetLines par clé réduites pour un niveau de step donné
     * va construire les splittedKeys si nécessaire
     * @param scen scenario cible
     * @param level niveau de l'ancêtre
     * @param pAncestorKey clé de l'ancêtre
     * @returns la map des lignes filles
     */
    public getWidgetLinesByLevel(scen: string, level: number, pAncestorKey: string): Map<string, WidgetLine> {
        return this.getAllWidgetLinesByLevel(scen, level).get(pAncestorKey) || null;
    }

    /**
     * ajout d'une ligne
     * @param widgetLine 
     */
    public addLine(widgetLine: WidgetLine): void {

        if (!this.lines.has(widgetLine.scen)) {
            this.lines.set(widgetLine.scen, new Map());
        }
        this.lines.get(widgetLine.scen).set(widgetLine.key, widgetLine);

        if (!!this._widgetLinesByLevels?.has(widgetLine.scen)) { // ajout à la map par levels si existe
            [...this._widgetLinesByLevels.get(widgetLine.scen).keys()].forEach((level: number) => {

                // split de la clé si pas encore fait
                if (!widgetLine.bl.splittedKey) { // si le split n'est pas encore fait
                    widgetLine.bl.splittedKey = widgetLine.bl.key.split(WizConstants.KEY_SEPARATOR);
                }

                let motherKey: string = widgetLine.bl.splittedKey.slice(0, this.vision.keySizeByLevel.get(level)).join(WizConstants.KEY_SEPARATOR);
                if (!this._widgetLinesByLevels.get(widgetLine.scen).get(level).has(motherKey)) {
                    this._widgetLinesByLevels.get(widgetLine.scen).get(level).set(motherKey, new Map());
                }
                this._widgetLinesByLevels.get(widgetLine.scen).get(level).get(motherKey).set(widgetLine.key, widgetLine);
            });
        };

    }

    /**
     * ajout des lignes sur retour de service
     * @param widgetLines 
     */
    public addLines(widgetLines: WidgetLine[]): void {
        widgetLines.forEach((wl: WidgetLine) => {
            this.addLine(wl);
        });
    }

    // suppression d'une ligne
    public deleteLine(widgetLine: WidgetLine): void {

        this.lines.get(widgetLine.scen)?.delete(widgetLine.key);

        if (!!this._widgetLinesByLevels?.has(widgetLine.scen)) { // suppression de la map par level
            [...this._widgetLinesByLevels.get(widgetLine.scen).keys()].forEach((level: number) => {
                // split de la clé si pas encore fait
                if (!widgetLine.bl.splittedKey) { // si le split n'est pas encore fait
                    widgetLine.bl.splittedKey = widgetLine.bl.key.split(WizConstants.KEY_SEPARATOR);
                }

                let motherKey: string = widgetLine.bl.splittedKey.slice(0, this.vision.keySizeByLevel.get(level)).join(WizConstants.KEY_SEPARATOR);
                this._widgetLinesByLevels.get(widgetLine.scen).get(level).get(motherKey)?.delete(widgetLine.key);
            });
        }

    }

    // suppression de ligne sur retour de service
    public deleteLines(widgetLines: WidgetLine[]): void {
        widgetLines.forEach((wl: WidgetLine) => {
            this.deleteLine(wl);
        });
    }
}
